/** @jsxImportSource @emotion/react */
import {
  EuiButton,
  EuiButtonEmpty,
  EuiForm,
  EuiModalBody,
  EuiModalFooter,
  EuiModalHeader,
  EuiModalHeaderTitle,
  EuiSpacer,
  EuiText,
  htmlIdGenerator,
} from "@inscopix/ideas-eui";
import { useRef, useState } from "react";
import { addUtilityToastFailure } from "utils/addUtilityToastFailure";
import { addUtilityToastSuccess } from "utils/addUtilityToastSuccess";
import { useDataTableContext } from "pages/gdt/store/DataTableProvider";
import assert from "assert";
import { FieldTable } from "../fields/FieldTable";
import { FieldColumnName } from "../fields/FieldColumnName";
import { FieldColumnMulti } from "../fields/FieldColumnMulti";

type Fields = {
  tableId: {
    value: string | undefined;
    isValid: boolean;
  };
  headerName: {
    value: string;
    isValid: boolean;
    isDirty: boolean;
  };
  columnIds: {
    value: string[];
    isValid: boolean;
  };
};

const INITIAL_FIELDS: Fields = {
  tableId: {
    value: undefined,
    isValid: false,
  },
  headerName: {
    value: "",
    isValid: false,
    isDirty: false,
  },
  columnIds: {
    value: [],
    isValid: true,
  },
};

interface ModalLinkDataTableProps {
  onBack: () => void;
  onClose: () => void;
}

/** Component that renders a modal for linking data tables */
export const ModalLinkDataTable = ({
  onBack,
  onClose,
}: ModalLinkDataTableProps) => {
  const selectedTableId = useDataTableContext((s) => s.selectedTableId);
  const linkDataTable = useDataTableContext((s) => s.linkDataTable);
  const headerNameFieldRef = useRef<HTMLInputElement>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [formId] = useState(htmlIdGenerator()());
  const [fields, setFields] = useState(INITIAL_FIELDS);

  /**
   * Shifts focus to the name field.
   *
   * This is tried multiple times because EuiSuperSelect (used by
   * FieldFileMetadatum) refocuses itself after onChange is called. I believe
   * this is a bug in EUI but I think this is the best option in the absence of
   * a long-term fix.
   */
  const focusNameField = () => {
    const timer = setInterval(() => {
      if (document.activeElement !== headerNameFieldRef.current) {
        headerNameFieldRef.current?.focus();
      } else {
        clearInterval(timer);
      }
    });
  };

  const handleSubmit = async () => {
    try {
      setIsLoading(true);
      assert(selectedTableId !== undefined);
      const sourceTableId = fields.tableId.value;
      assert(sourceTableId !== undefined);
      const headerName = fields.headerName.value;
      assert(headerName !== undefined);
      const { error } = await linkDataTable({
        tableId: selectedTableId,
        headerName,
        sourceTableId,
        sourceColumnIds: fields.columnIds.value,
      });
      assert(error === undefined);
      addUtilityToastSuccess("Data table linked");
    } catch (error) {
      addUtilityToastFailure("Failed to link data table");
    } finally {
      setIsLoading(false);
      onClose();
    }
  };

  return (
    <>
      <EuiModalHeader>
        <EuiModalHeaderTitle component="h3">
          Link Data Table
        </EuiModalHeaderTitle>
      </EuiModalHeader>

      <EuiModalBody>
        <EuiForm
          id={formId}
          component="form"
          role="form"
          onSubmit={(e) => {
            e.preventDefault();
            void handleSubmit();
          }}
        >
          <EuiText size="s">
            <p>Link rows from another data table with rows in this table.</p>
          </EuiText>

          <EuiSpacer />

          <FieldTable
            filter={{
              includeTable: (table) =>
                table.kind === "data" && table.id !== selectedTableId,
            }}
            value={fields.tableId.value}
            onChange={(value) => {
              // Update state
              setFields((prevFields) => {
                const newFields = { ...prevFields };

                // Set new table ID
                newFields.tableId = {
                  value: value?.id,
                  isValid: value?.id !== undefined,
                };

                // Reset columns field
                newFields.columnIds = {
                  value: [],
                  isValid: true,
                };

                // Auto-fill name field if not previously edited
                if (value !== undefined && !fields.headerName.isDirty) {
                  newFields.headerName = {
                    value: value.name,
                    isValid: value.name !== "",
                    isDirty: false,
                  };
                }

                return newFields;
              });

              // Shift focus to name field
              focusNameField();
            }}
          />

          {fields.tableId.value !== undefined && (
            <>
              <FieldColumnName
                ref={headerNameFieldRef}
                label="Header name"
                placeholder=""
                value={fields.headerName.value}
                onChange={(field) => {
                  setFields((prevFields) => {
                    const newFields = { ...prevFields };
                    newFields.headerName = {
                      value: field.value,
                      isValid: field.isValid,
                      isDirty: true,
                    };
                    return newFields;
                  });
                }}
              />
              <FieldColumnMulti
                tableId={fields.tableId.value}
                value={fields.columnIds.value}
                isColumnAlwaysSelected={(column) => column.isIdColumn}
                includeColumn={() => true}
                onChange={(columnIds) => {
                  setFields((prevFields) => {
                    const newFields = { ...prevFields };
                    newFields.columnIds = {
                      value: columnIds,
                      isValid: true,
                    };
                    return newFields;
                  });
                }}
              />
            </>
          )}
        </EuiForm>
      </EuiModalBody>

      <EuiModalFooter>
        <EuiButtonEmpty iconType="arrowLeft" onClick={onBack}>
          Back
        </EuiButtonEmpty>
        <EuiButton
          type="submit"
          form={formId}
          isLoading={isLoading}
          fill
          disabled={Object.values(fields).some((f) => !f.isValid)}
        >
          Insert
        </EuiButton>
      </EuiModalFooter>
    </>
  );
};
